/**
 * 86exwdkr5 (PR-1) - Offline canonical store (Dexie / IndexedDB).
 *
 * This is the source of truth for read views when offline. TanStack
 * Query's `queryFn` hydrates from here when the network is down and
 * writes through to here when the network is up. PR-2 will add an
 * `outbox` table for queued mutations.
 *
 * Schema versioning rules
 * ───────────────────────
 * - NEVER drop a table or column. Add new versions with `.upgrade()`
 *   migrations only. A queued write encoded under v1 must still be
 *   replayable after the app upgrades to v2.
 * - Every queued mutation (PR-2) will carry a `schemaVersion` field so
 *   the replay handler can decide whether to retransform or quarantine.
 * - On `open()` failure (e.g. iOS Private Browsing rejects IDB), the
 *   app falls back to online-only mode - see `useOfflineDb`.
 */

import Dexie, { type Table } from 'dexie'

// ── Row types stored in IDB ────────────────────────────────────────────────

export interface CachedBooking {
  id: number
  host_id: number
  organization_id: number
  // Tab tag this row was last fetched under ("arriving" | "in_house" |
  // "departing"). A single booking can appear in multiple tabs across a
  // multi-day stay; we store the latest snapshot per (id, tab).
  tab: string
  date: string // refDate (YYYY-MM-DD) the row was fetched under
  // The whole booking blob from the API - we don't decompose it here
  // because the FE renders straight off this shape. Saves a copy step
  // on every read.
  data: unknown
  // ETag from the server response (PR-2 will send `If-Match` on
  // mutations). Stored as part of PR-1 so PR-2 doesn't need a migration.
  etag: string | null
  cached_at: number // Date.now() at write time
}

export interface CachedHousekeepingRoom {
  id: number
  host_id: number
  organization_id: number
  data: unknown
  etag: string | null
  cached_at: number
}

export interface CachedBookingSummary {
  // composite key: host_id + organization_id + date
  key: string
  host_id: number
  organization_id: number
  date: string
  arriving: number
  in_house: number
  departing: number
  /** Unfiltered org booking count (the "All" chip). Optional: rows cached
   *  before the field existed have no value; Dexie needs no migration. */
  total?: number
  cached_at: number
}

/**
 * The host's property list, cached so a reload while offline can still
 * resolve a selected property.
 *
 * This one matters more than the other cached reads. Every host page
 * renders inside `OrgGuard` and every cached query is keyed on
 * `organization_id`, so without a property the app has nothing to ask
 * for: the bookings and housekeeping rows sit in IDB, intact and
 * unreachable, and the operator sees "No property selected" rather than
 * the offline board they were promised.
 */
export interface CachedOrganization {
  id: number
  host_id: number
  // Position in the list as the server returned it. Preserved so the
  // offline fallback picks the same default property as the online path
  // (orgs[0]) instead of whatever order IDB hands back.
  sort_index: number
  data: unknown
  cached_at: number
}

/**
 * A whole list response, cached verbatim under a caller-built key.
 *
 * The per-entity tables above only cover the three dated front-desk tabs.
 * The reservations list (including its All tab) is a paged, filtered query
 * whose response shape is `{results, count, page, ...}`, and it had no
 * offline cache at all: offline it went straight to the network, failed,
 * and rendered an empty list, which reads as "you have no reservations"
 * rather than "we cannot reach the server".
 *
 * Keyed by a stable serialization of (host, org, endpoint, params), so a
 * filter combination the operator actually used is the one available
 * offline. Capped and pruned oldest-first: filters are open-ended and an
 * unbounded table would eventually hit the quota that
 * `purgeCachedReads` exists to relieve.
 */
export interface CachedListSnapshot {
  key: string
  host_id: number
  data: unknown
  cached_at: number
}

/**
 * The signed-in user's host context: the host record plus the permission
 * payload every `useCan` reads.
 *
 * Cached because the permission hooks fail CLOSED - `usePermissionV2`
 * returns false when the context is missing - so losing it offline does
 * not open anything, it silently removes every button on the page with no
 * way for the operator to tell that from a bug. It previously lived only
 * in the service worker's runtime cache, which expires at 72 h and is
 * dropped on `purgeOnQuotaError`.
 *
 * Keyed by `user_id` and cleared on logout: this row decides what the UI
 * lets someone do, so it must never outlive the session that fetched it
 * or be readable by the next person to use the device.
 */
export interface CachedHostContext {
  user_id: number
  data: unknown
  cached_at: number
}

/**
 * Outbox table - placeholder schema for PR-2. We define it now so PR-1
 * users don't need a schema migration when PR-2 ships. The outbox row
 * carries enough metadata to replay on reconnect (PR-2 implements the
 * replay loop).
 */
export interface OutboxEntry {
  // Auto-incrementing local id. Server-assigned ids come back from the
  // replay handler and update via `idempotency_key`.
  id?: number
  idempotency_key: string // UUIDv4, client-generated
  endpoint: string // path + method, e.g. 'POST /api/v1/.../check-in/'
  method: string
  payload: unknown
  // ETag of the target row at enqueue time, sent as `If-Match` on replay.
  // null when not applicable (e.g. POST create).
  if_match: string | null
  // status drives the replay state machine + outbox drawer view.
  status: 'PENDING' | 'IN_FLIGHT' | 'SYNCED' | 'CONFLICT' | 'FAILED'
  attempts: number
  enqueued_at: number
  last_attempt_at: number | null
  last_error: string | null
  // Tag the FE uses to render a friendly label in the drawer
  // (e.g. 'Check-in: Mr. Sharma · #4521'). Stored at enqueue time
  // because the cached row may have been evicted by the time the
  // drawer renders.
  label: string
  // Schema version of the encoded `payload`. If the app updates to a
  // payload shape the replay handler can't understand, the entry is
  // quarantined rather than silently dropped.
  schema_version: number
}

/**
 * Singleton metadata rows: heartbeat timestamps, persist-storage status,
 * last-successful-refresh, schema version, install date, etc. Keyed by
 * `name` so callers do simple get/put.
 */
export interface MetaRow {
  name: string
  value: string
  updated_at: number
}

// ── Dexie wrapper ──────────────────────────────────────────────────────────

class OfflineDB extends Dexie {
  bookings!: Table<CachedBooking, [number, string, string]>
  housekeepingRooms!: Table<CachedHousekeepingRoom, number>
  bookingSummaries!: Table<CachedBookingSummary, string>
  organizations!: Table<CachedOrganization, number>
  hostContext!: Table<CachedHostContext, number>
  listSnapshots!: Table<CachedListSnapshot, string>
  outbox!: Table<OutboxEntry, number>
  meta!: Table<MetaRow, string>

  constructor() {
    super('basiyo-offline')

    // ──────────────────────────────────────────────────────────────────
    // v1 - initial schema. PR-1 read path uses bookings /
    // housekeepingRooms / bookingSummaries / meta. PR-2 will start
    // writing into outbox.
    //
    // Compound primary keys keep us from needing a secondary index for
    // multi-tab lookups: `[id+tab+date]` lets us upsert per-tab without
    // scanning.
    // ──────────────────────────────────────────────────────────────────
    this.version(1).stores({
      bookings: '[id+tab+date], host_id, organization_id, tab, date, cached_at',
      housekeepingRooms: 'id, host_id, organization_id, cached_at',
      bookingSummaries: 'key, host_id, organization_id, date, cached_at',
      outbox: '++id, idempotency_key, status, enqueued_at',
      meta: 'name, updated_at',
    })

    // ──────────────────────────────────────────────────────────────────
    // v2 - cache the host's property list.
    //
    // Purely additive, per the schema rules at the top of this file: v1
    // is left exactly as declared above so a client mid-upgrade with a
    // queued write can still replay it. Dexie carries every existing
    // table forward, so no `.upgrade()` body is needed to add a table -
    // one is only required when existing rows must be transformed.
    // ──────────────────────────────────────────────────────────────────
    this.version(2).stores({
      organizations: 'id, host_id, sort_index, cached_at',
    })

    // ──────────────────────────────────────────────────────────────────
    // v3 - cache the host context (host record + permission payload).
    // Additive, same rules as v2.
    // ──────────────────────────────────────────────────────────────────
    this.version(3).stores({
      hostContext: 'user_id, cached_at',
    })

    // ──────────────────────────────────────────────────────────────────
    // v4 - verbatim list-response snapshots (reservations list + its All
    // tab, which the per-entity tables never covered). Additive.
    // ──────────────────────────────────────────────────────────────────
    this.version(4).stores({
      listSnapshots: 'key, host_id, cached_at',
    })
  }
}

let _db: OfflineDB | null = null
let _dbInitError: Error | null = null

/**
 * Lazy-open the DB. Wrapped in try/catch because Firefox Private Browsing
 * + Safari iOS Private mode reject IDB; callers fall back to online-only.
 * Returns null on init failure.
 */
export function getOfflineDb(): OfflineDB | null {
  if (_db) return _db
  if (_dbInitError) return null
  try {
    _db = new OfflineDB()
    return _db
  } catch (err) {
    _dbInitError = err instanceof Error ? err : new Error(String(err))
    if (typeof console !== 'undefined') {
      console.warn('[offline] IDB init failed; running online-only', _dbInitError)
    }
    return null
  }
}

/**
 * Wipe the re-fetchable read caches. Used by 'switch property' so the
 * cached arrivals/in-house for the previous property don't leak, and by
 * the quota-reclaim path.
 *
 * Two tables are deliberately spared:
 *   - `outbox`, because losing a queued write is the one thing we must
 *     not do.
 *   - `organizations`, because it is what lets a reload resolve a
 *     property at all. Dropping it to reclaim space would trade a few
 *     hundred bytes for the entire offline app: no property means
 *     `OrgGuard` blocks every page and every org-keyed query goes
 *     `enabled: false`, so the rows we just freed space for become
 *     unreachable anyway.
 */
export async function purgeCachedReads(): Promise<void> {
  const db = getOfflineDb()
  if (!db) return
  await db.transaction(
    'rw',
    [db.bookings, db.housekeepingRooms, db.bookingSummaries, db.listSnapshots],
    async () => {
      await db.bookings.clear()
      await db.housekeepingRooms.clear()
      await db.bookingSummaries.clear()
      // Snapshots are whole list responses: the biggest and most
      // re-fetchable thing in here, so the first to go under quota
      // pressure. Logout reaches them through this same function.
      await db.listSnapshots.clear()
    },
  )
}

/** Runtime cache the service worker fills for front-desk API reads. MUST
 *  match the `cacheName` in service-worker.ts (kept as a string on both
 *  sides because the SW bundle must not import Dexie via this module). */
const SW_FRONTDESK_CACHE = 'basiyo-frontdesk-reads-v1'

/**
 * Logout hygiene: drop every cached READ so the next session (possibly a
 * different user on the same front-desk machine) cannot be served the
 * previous session's bookings, housekeeping board, or /host/me
 * entitlements payload from either cache layer.
 *
 * Deliberately keeps the outbox: it holds the previous user's unsent
 * writes, which must still drain (they carry their own auth snapshot and
 * idempotency keys, and replay against the server's own permission check
 * either way). Fire-and-forget; never throws into the logout path.
 */
export async function purgeReadCachesOnLogout(): Promise<void> {
  // Only the host app registers the SW / fills Dexie; on the admin app
  // this would just create an empty IDB for nothing.
  if (__APP_TYPE__ !== 'host') return
  try {
    await purgeCachedReads()
    // Also the two tables `purgeCachedReads` deliberately spares, because
    // logout and quota-reclaim want opposite things from them. Under
    // storage pressure the property list and host context must survive
    // (the app cannot function without them); at logout they must not,
    // because the host context carries the permission payload the UI
    // gates on and a front-desk tablet is shared.
    const db = getOfflineDb()
    if (db) await Promise.all([db.organizations.clear(), db.hostContext.clear()])
  } catch {
    /* IDB unavailable (private mode) - nothing cached to leak */
  }
  try {
    if (typeof caches !== 'undefined') await caches.delete(SW_FRONTDESK_CACHE)
  } catch {
    /* CacheStorage unavailable - the SW never installed either */
  }
}

// ── Metadata helpers ───────────────────────────────────────────────────────

const HEARTBEAT_KEY = 'last_heartbeat'
const LAST_REFRESH_KEY = 'last_successful_refresh'
const INSTALL_AT_KEY = 'installed_at'
const PERSIST_STATUS_KEY = 'storage_persisted'

export async function recordHeartbeat(): Promise<void> {
  const db = getOfflineDb()
  if (!db) return
  await db.meta.put({ name: HEARTBEAT_KEY, value: String(Date.now()), updated_at: Date.now() })
}

export async function recordLastRefresh(): Promise<void> {
  const db = getOfflineDb()
  if (!db) return
  await db.meta.put({ name: LAST_REFRESH_KEY, value: String(Date.now()), updated_at: Date.now() })
}

export async function getLastRefreshTime(): Promise<number | null> {
  const db = getOfflineDb()
  if (!db) return null
  const row = await db.meta.get(LAST_REFRESH_KEY)
  return row ? Number(row.value) : null
}

export async function recordInstall(): Promise<void> {
  const db = getOfflineDb()
  if (!db) return
  const existing = await db.meta.get(INSTALL_AT_KEY)
  if (existing) return
  await db.meta.put({ name: INSTALL_AT_KEY, value: String(Date.now()), updated_at: Date.now() })
}

export async function recordPersistStatus(persisted: boolean): Promise<void> {
  const db = getOfflineDb()
  if (!db) return
  await db.meta.put({
    name: PERSIST_STATUS_KEY,
    value: persisted ? 'true' : 'false',
    updated_at: Date.now(),
  })
}

const KEEPALIVE_KEY = 'last_keepalive'
const KEEPALIVE_INTERVAL_MS = 24 * 60 * 60 * 1000 // 24 h

/**
 * Keep the IndexedDB store warm so Safari's 7-day idle-eviction timer
 * never fires on an iPad/Mac left at the front desk over a long weekend.
 *
 * Two defences, both cheap and idempotent:
 *   1. A dated write to the `meta` table at most once per 24 h - touching
 *      the DB resets the eviction clock every time the app is opened.
 *   2. A re-request of `navigator.storage.persist()`. Once granted, a
 *      persisted store is exempt from eviction entirely; re-asking is a
 *      no-op when already granted but re-arms it if the grant lapsed.
 *
 * Call on `visibilitychange → visible` (front desk reopening the tab).
 * No-ops when IDB is unavailable.
 */
export async function touchKeepAlive(): Promise<void> {
  const db = getOfflineDb()
  if (!db) return
  const existing = await db.meta.get(KEEPALIVE_KEY).catch(() => undefined)
  const last = existing ? Number(existing.value) : 0
  if (Date.now() - last < KEEPALIVE_INTERVAL_MS) return
  await db.meta
    .put({ name: KEEPALIVE_KEY, value: String(Date.now()), updated_at: Date.now() })
    .catch(() => undefined)
  if (typeof navigator !== 'undefined' && navigator.storage?.persist) {
    try {
      await navigator.storage.persist()
    } catch {
      // best-effort - persist() can reject in private mode; ignore.
    }
  }
}
